/* eslint-disable no-param-reassign */

import Fn from './comparison';

/*
 * Power(base, exponent)
 *
 * Calculates a number raised to a power.
 *
 * SAMPLES
 * Power(2, 4) -> 16
 * Power(5, 2) -> 25
 *
 * INPUT
 * base: The base number to be raised to a power.
 * exponent: The exponent used to raise the base number by.
 *
 * OUTPUT
 * Returns the given base number raised to the given exponent.
 */
function Power(base, exponent) {
  if (base === null || base === undefined || exponent === null || exponent === undefined) {
    return 0;
  }
  base = +base;
  exponent = +exponent;

  return base ** exponent;
}

/*
 * Multiply(value, �)
 *
 * Multiplies all the numbers given as arguments and returns the product.
 *
 * SAMPLES
 * Multiply(2, 5) -> 10
 * Multiply(5, 3, 0.5) -> 7.5
 *
 * INPUT
 * values: The numbers to multiply.
 *
 * OUTPUT
 * Returns the mathematical product of all values received.
 */
function Multiply(...values) {
  if (values == null || values?.length === 0) return 0;

  let result = +values[0];
  if (isNaN(result)) return 0;

  for (let i = 1; i < values.length; i++) {
    if (values[i] == null || isNaN(values[i])) {
      result = 0;
      break;
    }
    result *= +values[i];
  }

  return result;
}

/*
 * Divide(dividend, divisor)
 *
 * Divide one number by another.
 * Divide(6, 2) -> 3
 * Divide(7, 2) -> 3.5
 *
 * INPUT
 * dividend: A number to divide
 * divisor: A number to divide dividend by
 *
 * OUTPUT
 * Returns dividend divided by divisor.
 */
function Divide(...values) {
  if (values == null || values.length === 0) return 0;

  let result = +values[0];
  if (isNaN(result)) return 0;

  for (let i = 1; i < values.length; i++) {
    if (values[i] == null) {
      result = 0;
      break;
    }
    if (+values[i] === 0) continue;
    result /= +values[i];
  }

  return result;
}

/*
 * Random(min, max)
 *
 * This function returns a randomised number between two values (both inclusive)
 *
 * SAMPLES
 * Random(1, 10)
 * Random(0, @number)
 *
 * INPUT
 * min: An integer value or variable
 * max: An integer value or variable
 *
 * OUTPUT
 * Returns a randomised number between two values(both inclusive)
 */
function Random(min, max) {
  if (min === null || min === undefined) {
    min = Number.MIN_SAFE_INTEGER;
  } else {
    min = +min;
  }
  if (max === null || max === undefined) {
    max = Number.MAX_SAFE_INTEGER;
  } else {
    max = +max;
  }

  return Math.floor(Math.random() * (max - min + 1) + min);
}

/*
 * Round(value, places, mode)
 *
 * Round a number.
 *
 * SAMPLES
 * Round(3.14159) -> 3
 * Round(3.14159, 1) -> 3.1
 * Round(3.14159, 2) -> 3.14
 * Round(34.99) -> 35
 *
 * INPUT
 * value: The number to round.
 * places: Optional.The number of decimal places to round to. Default value is zero.
 *
 * OUTPUT
 * Returns the value rounded to the specific number of decimal places.
 */
function Round(value, places, mode = null) {
  if (value === null || value === undefined || value.length === 0) {
    return 0;
  }
  value = +value;

  if (places >= 0) {
    return Number(value.toFixed(places));
  }

  places = Abs(places);
  var temp = value / Math.pow(10, places);
  temp = Round(temp, 0);
  return temp * Math.pow(10, places);
}

/*
 * Sum(values, �)
 *
 * Sums all the numbers given as arguments and returns the result.
 *
 * SAMPLES
 * Sum(6, 3) -> 9
 * Sum(7, 1, 0.5) -> 8.5
 * Sum(5, -1) -> 4
 *
 * INPUT
 * values: The numbers to add.
 *
 * OUTPUT
 * Returns the mathematical sum of all values received.
 */
function Sum(...values) {
  if (values == null || values?.length === 0) return 0;

  let result = +values[0];
  if (isNaN(result)) result = 0;

  if (isNaN(result)) result = 0;
  for (let i = 1; i < values.length; i++) {
    result += isNaN(+values[i]) ? 0 : +values[i];
  }

  return result;
}

/*
 * Subtract(value, �)
 *
 * Subtracts all the numbers given as arguments and returns the result.
 *
 * SAMPLES
 * Subtract(6, 3) -> 3
 * Subtract(7, 1, 0.5) -> 5.5
 *
 * INPUT
 * values: The numbers to subtract.
 *
 * OUTPUT
 * Returns the mathematical subtract of all values received.
 */
function Subtract(...values) {
  if (values == null || values?.length === 0) return 0;

  let result = +values[0];
  if (isNaN(result)) result = 0;

  if (isNaN(result)) result = 0;
  for (let i = 1; i < values.length; i++) {
    result -= isNaN(+values[i]) ? 0 : +values[i];
  }

  return result;
}

/*
 * ToFloat(value, places)
 *
 * Converts a string or integer to a floating point number.
 *
 * SAMPLES
 * ToFloat("10.45") -> 10.45
 * ToFloat("10.4571", 3) -> 10.467
 * ToFloat("99.00") -> 99.00
 *
 * INPUT
 * value: The value to be converted to floating point number.
 * places: Optional.The number of decimal places to round to. Default value is 2.
 *
 * OUTPUT
 * Returns the value as a floating point number.
 */
function ToFloat(value, places) {
  if (value === null || value === undefined || value.length === 0) {
    return 0;
  }
  value = +value;
  return parseFloat(value).toFixed(places);
}

/*
 * ToInteger(value)
 *
 * Converts a string or number to an integer.
 *
 * SAMPLES
 * ToInteger("10.45") -> 10
 *
 * INPUT
 * value: The value to be converted to integer.
 *
 * OUTPUT
 * Returns the value as a integer.
 */
function ToInteger(value) {
  if (value === null || value === undefined || value.length === 0) {
    return 0;
  }
  return parseInt(value);
}

/*
 * Trunc(value)
 *
 * Cuts off the dot and the digits to the right of a number.
 *
 * SAMPLES
 * Trunc(3.14159) -> 3
 * Trunc(34.99) -> 34
 * Trunc(-3.67) -> 3
 *
 * INPUT
 * value: The number to trunc.
 *
 * OUTPUT
 * Returns the integer part of value by removing any fractional digits.
 */
function Trunc(value) {
  if (value === null || value === undefined || value.length === 0) {
    return 0;
  }
  return Math.trunc(value);
}

/*
 * Abs(number)
 *
 * That is, it returns x if x is positive or zero, and the negation of x if x is negative.
 *
 * SAMPLES
 * Abs(-3.14159) -> 3.14159
 * Abs(34.99) -> 34.99
 *
 * INPUT
 * value: The number
 *
 * OUTPUT
 * returns the absolute value of a number.
 */
function Abs(number) {
  if (number === null || number === undefined || number.length === 0) {
    return 0;
  }
  return Math.abs(number);
}

/*
 * Sqrt(number)
 *
 * returns the square root of a number
 *
 * SAMPLES
 * Sqrt(9) -> 3
 * Sqrt(2) -> 1.414213562373095
 *
 * INPUT
 * value: The number
 *
 * OUTPUT
 * The square root of the given number. If the number is negative, NaN is returned.
 */
function Sqrt(number) {
  if (number === null || number === undefined || number.length === 0 || number === 0) {
    return 0;
  }
  return Math.sqrt(number);
}

/*
 * Max(values, �)
 *
 * Compare all argument values and return the largest number
 *
 * SAMPLES
 * Sum(6, 3) -> 6
 * Sum(7, 1, 0.5) -> 7
 * Sum(5, -1) -> 5
 *
 * INPUT
 * values: The numbers to compare
 *
 * OUTPUT
 * Returns the largest number
 */
function Max(...values) {
  if (values === null || values === undefined || values.length === 0) {
    return '';
  }
  var result = +values[0];
  for (let i = 1; i < values.length; i++) {
    if (+values[i] > result) {
      result = +values[i];
    }
  }
  return result;
}

/*
 * Min(values, �)
 *
 * Compare all argument values and return the smallest number
 *
 * SAMPLES
 * Sum(6, 3) -> 3
 * Sum(7, 1, 0.5) -> 0.5
 * Sum(5, -1) -> -1
 *
 * INPUT
 * values: The numbers to compare
 *
 * OUTPUT
 * Returns the smallest number
 */
function Min(...values) {
  if (values === null || values === undefined || values.length === 0) {
    return '';
  }
  var result = +values[0];
  for (let i = 1; i < values.length; i++) {
    if (+values[i] < result) {
      result = +values[i];
    }
  }
  return result;
}

/*
 * Ceiling(number,significance,mode)
 *
 * Rounds a number up to the nearest integer or to the nearest multiple of significance.
 *
 * SAMPLES
 * Ceiling(1, 10) -> 10
 * Ceiling(2.5, 10) -> 10
 * Ceiling(51, 5) -> 55
 *
 * INPUT
 * number: Number must be less than 9.99E+307 and greater than -2.229E-308.
 * significance: Optional. The multiple to which Number is to be rounded.
 * mode: Optional. For negative numbers, controls whether Number is rounded toward or away from zero.
 *
 * OUTPUT
 * Returns the nearest integer or to the nearest multiple of significance
 */
function Ceiling(number, significance, mode = null) {
  if (number === null || number === undefined || number.length === 0) {
    return '';
  }
  if (significance === null || significance === undefined || significance.length == 0) {
    significance = 1;
  }
  return Math.ceil(number / significance) * significance;
}

//*
// * IsNumber(value)
// *
// * returns true or false if value is number
// *
// * SAMPLES
// * IsNumber(9) -> true
// * IsNumber("9") -> true
// * IsNumber("a9") -> false
// *
// * INPUT
// * value: The value you want to check if is number
// *
// * OUTPUT
// * if value is number true else false
// */
//function IsNumber(value) {
//    if (isNaN(value)) {
//        return false;
//    }
//    if (typeof value === 'number' && isFinite(value) || Number.isInteger(value)) {
//        return true;
//    }

//    return false;
//}

const regexMatchNumbers = /^-?\d*\.?\d*$/;

function removeSeparators(value, oldSeparator = ',', newSeparator = '') {
  try {
    return value.replaceAll(oldSeparator, newSeparator);
  } catch (e) {}

  return value;
}

function ConvertToNumber(value) {
  if (Fn.IsNullOrEmpty(value)) {
    return 0;
  }

  if (value.length > 50) {
    return value;
  }

  value = removeSeparators(value);

  try {
    const matches = value.match(regexMatchNumbers);
    if (matches === null || matches === undefined || matches.length === 0) {
      return value;
    }
  } catch (e) {}

  return Number(value);
}

export default {
  Power,
  Multiply,
  Divide,
  Random,
  Round,
  Sum,
  Subtract,
  ToFloat,
  ToInteger,
  Trunc,
  Abs,
  Sqrt,
  Max,
  Min,
  Ceiling,
  ConvertToNumber,
};
